import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Login from "./Login";
import Dashboard from "./components/Dashboard";
import TermsOfService from "./TermsOfService";
import PrivacyPolicy from "./PrivacyPolicy";
import SupportPage from "./SupportPage";
import AccountDeletion from "./AccountDeletion";
import RequestAccountPage from "./RequestAccountPage";
import AcceptInvite from "./AcceptInvite";
import ResetPassword from "./ResetPassword";
import api from "./api";

export default function App() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem("token") || null);
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  });
  // The original admin account when an impersonation ("View as") session is active.
  const [impersonator, setImpersonator] = useState(() => {
    const raw = localStorage.getItem("impersonatorUser");
    return raw ? JSON.parse(raw) : null;
  });

  useEffect(() => {
    // If token was saved to localStorage previously, keep user in sync as well
    if (token) {
      localStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      if (user !== null) setUser(null);
    }
  }, [token, user]);

  const handleLogout = () => {
    localStorage.removeItem('impersonatorToken');
    localStorage.removeItem('impersonatorUser');
    setImpersonator(null);
    setToken(null);
    navigate('/login');
  };

  // Begin an admin "View as" session: stash the admin session for return, then
  // swap the live token+user to the target so the whole app reflects them.
  const handleImpersonate = async (userId) => {
    const { token: t, user: u } = await api.impersonate(userId);
    localStorage.setItem('impersonatorToken', token);
    localStorage.setItem('impersonatorUser', JSON.stringify(user));
    setImpersonator(user);
    setToken(t);
    setUser(u);
    localStorage.setItem('token', t);
    localStorage.setItem('user', JSON.stringify(u));
    navigate('/overview');
  };

  // Return to the admin account. Server re-issues from the signed impersonatedBy
  // claim; fall back to the stashed admin session if that call fails.
  const handleStopImpersonation = async () => {
    try {
      const { token: t, user: u } = await api.stopImpersonating();
      setToken(t);
      setUser(u);
      localStorage.setItem('token', t);
      localStorage.setItem('user', JSON.stringify(u));
    } catch (err) {
      const savedToken = localStorage.getItem('impersonatorToken');
      const savedUser = localStorage.getItem('impersonatorUser');
      if (savedToken && savedUser) {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
        localStorage.setItem('token', savedToken);
        localStorage.setItem('user', savedUser);
      }
    }
    localStorage.removeItem('impersonatorToken');
    localStorage.removeItem('impersonatorUser');
    setImpersonator(null);
    navigate('/admin');
  };

  // Redirect to login on session expiry (any API call returning 401)
  useEffect(() => {
    const handleExpired = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('impersonatorToken');
      localStorage.removeItem('impersonatorUser');
      setImpersonator(null);
      setToken(null);
      navigate('/login', { replace: true });
    };
    window.addEventListener('auth:expired', handleExpired);
    return () => window.removeEventListener('auth:expired', handleExpired);
  }, [navigate]);

  const handleLoginSuccess = ({ token: t, user: u, remember }) => {
    setToken(t);
    setUser(u);
    if (remember) {
      localStorage.setItem("token", t);
      localStorage.setItem("user", JSON.stringify(u));
    }
    navigate((u?.role === 'ADMIN' || u?.role === 'SITE_MANAGER') ? '/admin' : '/overview');
  };

  const handleUserUpdate = (updatedUser) => {
    const merged = { ...user, ...updatedUser };
    setUser(merged);
    localStorage.setItem("user", JSON.stringify(merged));
  };

  const initials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((p) => p[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  return (
    <Routes>
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/support" element={<SupportPage />} />
      {/* Account deletion URL published on the Google Play store listing. The
          /delete-account alias covers the other spelling users tend to guess. */}
      <Route path="/account-deletion" element={<AccountDeletion />} />
      <Route path="/delete-account" element={<Navigate to="/account-deletion" replace />} />
      <Route path="/request-account" element={<RequestAccountPage />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/login"
        element={
          !token ? (
            <div className="app-container" style={{ padding: 0, fontFamily: "Inter, Arial, Helvetica, sans-serif" }}>
              <div style={{ width: "100%" }}>
                <Login onSuccess={handleLoginSuccess} />
              </div>
            </div>
          ) : (
            <Navigate to={(user?.role === 'ADMIN' || user?.role === 'SITE_MANAGER') ? '/admin' : '/overview'} replace />
          )
        } 
      />
      <Route 
        path="/*" 
        element={
          token ? (
            <Dashboard key={user?.id || 'anon'} user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} impersonator={impersonator} onImpersonate={handleImpersonate} onStopImpersonation={handleStopImpersonation} />
          ) : (
            <Navigate to="/login" replace />
          )
        } 
      />
    </Routes>
  );
}